import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskPreviewDetails } from '@hkm/components/TaskManagement/TaskPreview/domain/interfaces';

import { AcBadge } from '@ac/mobile-components/dist/components/badge';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import { AcFormElement } from '@ac/mobile-components/dist/components/form-element';
import { AcIcon } from '@ac/mobile-components/dist/components/icon';
import { AcText } from '@ac/mobile-components/dist/components/text';
import { AcTileShell } from '@ac/mobile-components/dist/components/tile';
import {
  Color,
  FlexDirection,
  Icon,
  TextSize,
  TextWeight
} from '@ac/mobile-components/dist/enums';

import './Details.css';

interface ComponentProps {
  task: TaskPreviewDetails;
}

const Details: FC<ComponentProps> = (props: ComponentProps) => {
  const { t } = useTranslation();
  const { task } = props;

  const taskTags = useMemo(
    () =>
      (task.taskTags || []).map(tag => (
        <AcBadge
          key={tag.id}
          badgeText={tag.code}
          backgroundColor={tag.color || Color.Black}
          className="ac-spacing-right-sm ac-spacing-botttom-sm"
        />
      )),
    [task.taskTags]
  );

  const reporter = useMemo(
    () =>
      task.reporterUser?.userFullName
        ? task.reporterUser?.userFullName
        : undefined,
    [task.reporterUser]
  );

  const assignee = useMemo(
    () =>
      task.assignedUser?.userFullName
        ? task.assignedUser?.userFullName
        : undefined,
    [task.assignedUser]
  );

  return (
    <AcTileShell className="task-details">
      <AcFlex direction={FlexDirection.column}>
        <AcFlex className="task-details-header">
          <AcIcon icon={Icon.Task} className="ac-spacing-right-sm" />
          <AcText
            uppercase={true}
            weight={TextWeight.Semibold}
            size={TextSize.Main2}
          >
            {t('TASK_MANAGEMENT.TASK_PREVIEW.TASK_DETAILS.TITLE')}
          </AcText>
        </AcFlex>
        <AcFormElement
          label={t('TASK_MANAGEMENT.TASK_PREVIEW.TASK_DETAILS.TASK_TYPE')}
          className="ac-spacing-bottom-sm ac-spacing-top-sm"
          testSelector="task-type"
        >
          {task.taskTypeDescription}
        </AcFormElement>
        <AcFormElement
          label={t('TASK_MANAGEMENT.TASK_PREVIEW.TASK_DETAILS.DEPARTMENT')}
          className="ac-spacing-bottom-sm"
          testSelector="task-department"
        >
          {task.assigneeDepartmentDescription ?? '-'}
        </AcFormElement>
        <AcFormElement
          label={t('TASK_MANAGEMENT.TASK_PREVIEW.TASK_DETAILS.DESCRIPTION')}
          className="ac-spacing-bottom-sm"
          testSelector="task-description"
        >
          {task.description ?? '-'}
        </AcFormElement>
        <AcFormElement
          label={t('TASK_MANAGEMENT.TASK_PREVIEW.TASK_DETAILS.TAGS')}
          className="ac-spacing-bottom-sm"
        >
          <AcFlex wrapped={true} testSelector="task-tags">
            {taskTags.length ? taskTags : '-'}
          </AcFlex>
        </AcFormElement>
        <AcFormElement
          label={t('TASK_MANAGEMENT.TASK_PREVIEW.TASK_DETAILS.REPORTER')}
          className="ac-spacing-bottom-sm"
          testSelector="task-reporter"
        >
          {reporter ?? '-'}
        </AcFormElement>
        <AcFormElement
          label={t('TASK_MANAGEMENT.TASK_PREVIEW.TASK_DETAILS.ASSIGNEE')}
          testSelector="task-assignee"
        >
          {assignee ?? '-'}
        </AcFormElement>
      </AcFlex>
    </AcTileShell>
  );
};

export default Details;



// WEBPACK FOOTER //
// ./src/components/TaskManagement/TaskPreview/Details/Details.tsx